import type {
  JobResult,
  UploadFileResponse,
  RunProcessingJobRequest,
  OutputColumn,
  CustomValidationCheck,
  OutputSchema,
  EngineConfiguration,
  EngineConfigurationSummary,
  EngineConfigurationSummaryPage,
  AddEngineConfigurationRequest,
  OutputSchemaSummaryPage,
  OutputSchemaSummary,
  AddOutputSchemaRequest,
  JobResultSummaryPage,
  CanPublishJobResponse,
  ReferenceDataTable,
  ReferenceDataTableSummaryPage,
} from '@/collateral-sdk';

import type { PagedParams } from '@/types';
import BaseService, { type QueryParams } from './BaseService';

export type UploadDocument = { input_file: File };

export interface EditChecksExportFilters {
  from?: string;
  to?: string;
  customer_account_key?: string;
  status?: string;
}

export interface EditChecksExportStartedResponse {
  export_id: string;
  status: string;
}

export interface EditChecksExportStatusResponse {
  export_id: string;
  status: string;
  download_url: string | null;
}

class CollateralProcessorService extends BaseService {
  uploadFile = async (formData: FormData): Promise<UploadFileResponse> => {
    return this.post<FormData, UploadFileResponse>({
      baseUrl: '/api/collateral-service',
      path: `processing/upload-file`,
      body: formData,
      contentType: 'multipart/form-data',
    });
  };

  runProcess = async (body: RunProcessingJobRequest): Promise<JobResult> => {
    return this.post<RunProcessingJobRequest, JobResult>({
      baseUrl: '/api/collateral-service',
      path: `processing/start`,
      body,
      contentType: 'application/json',
    });
  };

  getJobsSummaryPaginated = async (params?: PagedParams | QueryParams): Promise<JobResultSummaryPage> => {
    const queryParams = params as QueryParams;
    return this.get<JobResultSummaryPage>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs_summary`,
      params: queryParams,
    });
  };

  getJob = async ({ job_uuid }: { job_uuid: string | undefined }): Promise<JobResult | null> => {
    return this.get<JobResult>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/${job_uuid}`,
    });
  };

  getJobSummary = async ({ job_uuid }: { job_uuid: string | undefined }): Promise<JobResult | null> => {
    return this.get<JobResult>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/${job_uuid}/summary`,
    });
  };

  getConfigurationSummary = async (params?: PagedParams): Promise<EngineConfigurationSummaryPage> => {
    const queryParams = params as QueryParams;
    return this.get<EngineConfigurationSummaryPage>({
      baseUrl: '/api/collateral-service',
      path: `configuration/engine_configuration_summary`,
      params: queryParams,
    });
  };

  getConfiguration = async (key: string): Promise<EngineConfiguration> => {
    return this.get<EngineConfiguration>({
      baseUrl: '/api/collateral-service',
      path: `configuration/engine_configuration/${key}`,
    });
  };

  createConfiguration = async ({
    body,
  }: {
    body: AddEngineConfigurationRequest;
  }): Promise<EngineConfigurationSummary> => {
    return this.post<AddEngineConfigurationRequest, EngineConfigurationSummary>({
      baseUrl: '/api/collateral-service',
      path: `configuration/engine_configuration`,
      body,
      contentType: 'application/json',
    });
  };

  editConfiguration = async ({
    body,
  }: {
    body: AddEngineConfigurationRequest;
  }): Promise<EngineConfigurationSummary> => {
    return this.put<EngineConfiguration, EngineConfigurationSummary>({
      baseUrl: '/api/collateral-service',
      path: `configuration/engine_configuration/${body.key}`,
      body: body.engine_configuration,
      contentType: 'application/json',
    });
  };

  deleteConfiguration = async ({ key }: { key: string }): Promise<EngineConfigurationSummary> => {
    return this.delete<EngineConfigurationSummary>({
      baseUrl: '/api/collateral-service',
      path: `configuration/engine_configuration/${key}`,
    });
  };

  getOutputSchemaPaginatedSummary = async (params?: PagedParams): Promise<OutputSchemaSummaryPage> => {
    const queryParams = params as QueryParams;
    return this.get<OutputSchemaSummaryPage>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema_summary`,
      params: queryParams,
    });
  };

  getOutputSchema = async ({ key }: { key: string }): Promise<OutputSchema> => {
    return this.get<OutputSchema>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}`,
    });
  };

  createOutputSchema = async ({ body }: { body: AddOutputSchemaRequest }): Promise<OutputSchemaSummary> => {
    return this.post<AddOutputSchemaRequest, OutputSchemaSummary>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema`,
      body,
      contentType: 'application/json',
    });
  };

  editOutputSchema = async ({ body }: { body: AddOutputSchemaRequest }): Promise<OutputSchemaSummary> => {
    return this.put<OutputSchema, OutputSchemaSummary>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${body.key}`,
      body: body.output_schema,
      contentType: 'application/json',
    });
  };

  deleteOutputSchema = async ({ key }: { key: string }): Promise<OutputSchemaSummary> => {
    return this.delete<OutputSchemaSummary>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}`,
    });
  };

  editOutputSchemaColumn = async ({ key, body }: { key: string; body: OutputColumn }): Promise<OutputColumn> => {
    return this.put<OutputColumn, OutputColumn>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/column/${body.name}`,
      body,
      contentType: 'application/json',
    });
  };

  createOutputSchemaColumn = async ({ key, body }: { key: string; body: OutputColumn }): Promise<OutputColumn> => {
    return this.post<OutputColumn, OutputColumn>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/column`,
      body,
      contentType: 'application/json',
    });
  };

  deleteOutputSchemaColumn = async ({ key, name }: { key: string; name: string }): Promise<OutputColumn> => {
    return this.delete<OutputColumn>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/column/${name}`,
    });
  };

  editCustomValidationCheck = async ({
    key,
    body,
  }: {
    key: string;
    body: CustomValidationCheck;
  }): Promise<CustomValidationCheck> => {
    return this.put<CustomValidationCheck, CustomValidationCheck>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/custom_validation_check/${body.id}`,
      body,
      contentType: 'application/json',
    });
  };

  createCustomValidationCheck = async ({
    key,
    body,
  }: {
    key: string;
    body: CustomValidationCheck;
  }): Promise<CustomValidationCheck> => {
    return this.post<CustomValidationCheck, CustomValidationCheck>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/custom_validation_check`,
      body,
      contentType: 'application/json',
    });
  };

  toogleEnabledCustomValidationCheck = async ({
    key,
    check_id,
    body,
  }: {
    key: string;
    check_id: string;
    body: CustomValidationCheck;
  }): Promise<CustomValidationCheck> => {
    return this.put<CustomValidationCheck, CustomValidationCheck>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/custom_validation_check/${check_id}`,
      body,
      contentType: 'application/json',
    });
  };

  deleteCustomValidationCheck = async ({
    key,
    check_id,
  }: {
    key: string;
    check_id: string;
  }): Promise<CustomValidationCheck> => {
    return this.delete<CustomValidationCheck>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/${key}/custom_validation_check/${check_id}`,
    });
  };

  getOutputDownloadURL = async ({
    job_uuid,
    file_type,
  }: {
    job_uuid: string;
    file_type?: string;
  }): Promise<OutputSchema> => {
    return this.get<OutputSchema>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/${job_uuid}/output_download_url${file_type ? '?file_type=' + file_type : ''}`,
    });
  };

  getCanPublish = async ({ job_uuid }: { job_uuid: string }): Promise<CanPublishJobResponse> => {
    return this.get<CanPublishJobResponse>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/${job_uuid}/can_publish`,
    });
  };

  publish = async ({ job_uuid }: { job_uuid: string }): Promise<JobResult> => {
    return this.post<{}, JobResult>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/${job_uuid}/publish`,
    });
  };

  getRecentlyUsedConfigKeys = async (): Promise<Array<string>> => {
    return this.get<Array<string>>({
      baseUrl: '/api/collateral-service',
      path: `processing/recently_used_config_keys`,
    });
  };

  getReferenceDataTables = async (params?: PagedParams): Promise<ReferenceDataTableSummaryPage> => {
    const queryParams = params as QueryParams;
    return this.get<ReferenceDataTableSummaryPage>({
      baseUrl: '/api/collateral-service',
      path: `configuration/reference_data_table_summary`,
      params: queryParams,
    });
  };

  getReferenceDataTable = async (key: string): Promise<ReferenceDataTable> => {
    return this.get<ReferenceDataTable>({
      baseUrl: '/api/collateral-service',
      path: `configuration/reference_data_table/${key}`,
    });
  };

  createReferenceDataTable = async (body: {
    key: string;
    reference_data_table: ReferenceDataTable;
  }): Promise<ReferenceDataTable> => {
    return this.post<{ key: string; reference_data_table: ReferenceDataTable }, ReferenceDataTable>({
      baseUrl: '/api/collateral-service',
      path: `configuration/reference_data_table`,
      body,
      contentType: 'application/json',
    });
  };

  updateReferenceDataTable = async (key: string, body: ReferenceDataTable): Promise<ReferenceDataTable> => {
    return this.put<ReferenceDataTable, ReferenceDataTable>({
      baseUrl: '/api/collateral-service',
      path: `configuration/reference_data_table/${key}`,
      body,
      contentType: 'application/json',
    });
  };

  // Fetch columns for the hard-coded 'edit-checks' output schema using dedicated endpoint
  getEditCheckColumns = async (): Promise<OutputColumn[]> => {
    return this.get<OutputColumn[]>({
      baseUrl: '/api/collateral-service',
      path: `configuration/output_schema/edit-checks/columns`,
    });
  };

  // Start generating an edit check failures export in the background.
  startEditChecksExport = async (filters: EditChecksExportFilters): Promise<EditChecksExportStartedResponse> => {
    return this.post<undefined, EditChecksExportStartedResponse>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/export`,
      params: filters as QueryParams,
    });
  };

  // Poll the status of an edit checks export; returns a download URL once ready.
  getEditChecksExportStatus = async ({ export_id }: { export_id: string }): Promise<EditChecksExportStatusResponse> => {
    return this.get<EditChecksExportStatusResponse>({
      baseUrl: '/api/collateral-service',
      path: `processing/jobs/export/${export_id}`,
    });
  };
}

export default CollateralProcessorService;
